import React from 'react'
import clsx from 'clsx'
import { KTSVG, IconPaths } from '_metronic/helpers'

type Variant = 'warning' | 'danger' | 'info' | 'success'

type Props = {
  variant?: Variant
  title?: React.ReactNode
  children: React.ReactNode
  className?: string
  showIcon?: boolean
  /** `sm` para espaços estreitos (ex.: trilho lateral do atendimento). */
  size?: 'md' | 'sm'
  /** Ação do aviso (ex.: botão), à direita do texto; em tela estreita desce para baixo dele. */
  action?: React.ReactNode
  /** Papel ARIA do aviso, ex.: 'alert' para erro que precisa ser anunciado, 'note' para observação. */
  role?: React.AriaRole
}

const variantBgMap: Record<Variant, string> = {
  warning: 'bg-light-warning',
  danger: 'bg-light-danger',
  info: 'bg-light-info',
  success: 'bg-light-success',
}

const variantIconMap: Record<Variant, string> = {
  warning: IconPaths.Warning,
  danger: IconPaths.X,
  info: IconPaths.Info,
  success: IconPaths.CircleCheck,
}

export const InfoAlert: React.FC<Props> = ({
  variant = 'warning',
  title,
  children,
  className,
  showIcon = true,
  size = 'md',
  action,
  role,
}) => {
  const compacto = size === 'sm'

  return (
    <div
      className={clsx(
        'notice d-flex rounded',
        compacto ? 'p-3 mb-3' : 'p-5 mb-5',
        variantBgMap[variant],
        className
      )}
      style={{ borderLeft: `4px solid var(--bs-${variant})` }}
      role={role}
    >
      {showIcon && (
        <KTSVG
          path={variantIconMap[variant]}
          className={clsx(
            compacto ? 'svg-icon-2 me-2' : 'svg-icon-2hx me-4',
            `svg-icon-${variant}`
          )}
        />
      )}

      <div className='d-flex flex-wrap align-items-center justify-content-between gap-4 flex-grow-1'>
        <div className='d-flex flex-column'>
          {title && <span className='fw-bold text-gray-900 fs-6 mb-1'>{title}</span>}
          <div className='fs-7 text-gray-700'>{children}</div>
        </div>
        {action && <div className='flex-shrink-0'>{action}</div>}
      </div>
    </div>
  )
}
